/**
 * TeamSystem Impact - Material UI Theme
 * Tema basato sul Design System TeamSystem
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { itIT } from '@mui/material/locale';

// Colori TeamSystem Design System - Brand Colors
const teamSystemColors = {
  primary: {
    main: '#0066CC',      // TeamSystem Blue - Main brand color
    light: '#3399FF',     // Lighter blue
    dark: '#004C99',      // Darker blue
    contrastText: '#FFFFFF',
    50: '#E6F0FA',
    100: '#CCE0F5',
    200: '#99C2EB',
    300: '#66A3E0',
    400: '#3385D6',
    500: '#0066CC',
    600: '#0052A3',
    700: '#003D7A',
    800: '#002952',
    900: '#001429',
  },
  secondary: {
    main: '#1A3A5C',      // Navy - Header/Sidebar (darker TeamSystem blue)
    light: '#2D5A8A',     // Slate
    dark: '#0D1F33',      // Deep navy
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#ef4444',      // Red-500
    light: '#fee2e2',     // Red-100 (for backgrounds)
    dark: '#dc2626',      // Red-600
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#f59e0b',      // Amber-500
    light: '#fef3c7',     // Amber-100 (for backgrounds)
    dark: '#d97706',      // Amber-600
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#22c55e',      // Green-500
    light: '#dcfce7',     // Green-100 (for backgrounds)
    dark: '#16a34a',      // Green-600
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0066CC',      // Same as primary
    light: '#E6F0FA',
    dark: '#004C99',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#f9fafb',        // Page background
    100: '#f3f4f6',       // Card hover, subtle backgrounds
    200: '#9ca3af',       // Borders, dividers
    300: '#d1d5db',       // Disabled states
    400: '#9ca3af',       // Placeholder text
    500: '#525252',       // Secondary text (WCAG AA 7.0:1 on white)
    600: '#4b5563',       // Body text
    700: '#374151',       // Headings
    800: '#1f2937',       // Primary text
    900: '#111827',       // Emphasis
  },
};

// Data visualization colors
export const dataVizColors = {
  purple: {
    light: '#e9d5ff',
    main: '#a855f7',
    dark: '#7c3aed',
  },
  cyan: {
    light: '#a5f3fc',
    main: '#0066CC',
    dark: '#004C99',
  },
  indigo: '#6366f1',
  pink: '#ec4899',
  teal: '#14b8a6',
  orange: '#f97316',
};

// Gender distribution colors
export const genderColors = {
  women: '#a855f7',    // Purple
  men: '#0066CC',      // Cyan
  other: '#f97316',    // Orange
};

// Trend indicator colors
export const trendColors = {
  positive: '#22c55e',
  negative: '#ef4444',
  neutral: '#525252',  // WCAG AA 7.0:1 on white
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#171717',      // neutral-900 — matches shadcn --primary
      light: '#404040',     // neutral-700
      dark: '#0a0a0a',      // neutral-950
      contrastText: '#fafafa',
    },
    secondary: {
      main: '#f5f5f5',      // neutral-100 — matches shadcn --secondary
      light: '#fafafa',
      dark: '#e5e5e5',
      contrastText: '#171717',
    },
    error: teamSystemColors.error,
    warning: teamSystemColors.warning,
    success: teamSystemColors.success,
    info: {
      main: '#171717',
      light: '#f5f5f5',
      dark: '#0a0a0a',
      contrastText: '#fafafa',
    },
    grey: teamSystemColors.grey,
    background: {
      default: '#ffffff',   // matches shadcn --background
      paper: '#ffffff',     // matches shadcn --card
    },
    text: {
      primary: '#0a0a0a',   // neutral-950
      secondary: '#525252', // neutral-600 — WCAG AA 7.0:1 on white
      disabled: '#a3a3a3',  // neutral-400
    },
    divider: '#9ca3af',     // neutral-400 — WCAG 1.4.11 non-text contrast ≥ 3:1
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#111827',
    },
    h2: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: '#1f2937',
    },
    h3: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#1f2937',
    },
    h4: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#374151',
    },
    h5: {
      fontSize: '13px',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#374151',
    },
    h6: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: 1.6,
      color: '#4b5563',
    },
    body1: {
      fontSize: '14px',
      lineHeight: 1.5,
      color: '#374151',
    },
    body2: {
      fontSize: '13px',
      lineHeight: 1.4,
      color: '#4b5563',
    },
    caption: {
      fontSize: '12px',
      lineHeight: 1.4,
      color: '#525252',   // WCAG AA 7.0:1 on white
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '14px',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.05)',                                    // 1 - xs
    '0 1px 3px rgba(0, 0, 0, 0.05)',                                    // 2 - sm
    '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',    // 3 - md
    '0 10px 15px rgba(0, 0, 0, 0.05), 0 4px 6px rgba(0, 0, 0, 0.03)',  // 4 - lg
    '0 20px 25px rgba(0, 0, 0, 0.1), 0 8px 10px rgba(0, 0, 0, 0.04)',  // 5 - xl
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 6
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 7
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 8
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 9
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 10
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 11
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 12
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 13
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 14
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 15
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 16
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 17
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 18
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 19
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 20
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 21
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 22
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 23
    '0 25px 50px rgba(0, 0, 0, 0.12)',                                  // 24
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        // WCAG 2.4.7 — visible focus indicator for all interactive elements
        '*:focus-visible': {
          outline: '2px solid #0066CC',
          outlineOffset: '2px',
        },
        // MUI handles input/textarea focus via fieldset border
        'input:focus-visible, textarea:focus-visible': {
          outline: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: 600,
          transition: 'all 0.2s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
        containedPrimary: {
          backgroundColor: '#0066CC',
          '&:hover': {
            backgroundColor: '#004C99',
          },
        },
        outlined: {
          borderColor: '#0066CC',
          color: '#004C99',
          '&:hover': {
            backgroundColor: '#E6F0FA',
            borderColor: '#0066CC',
          },
        },
        text: {
          color: '#4b5563',
          '&:hover': {
            backgroundColor: '#f3f4f6',
            color: '#1f2937',
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '12px',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderRadius: 12,
          border: '1px solid #9ca3af',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: '#9ca3af', // WCAG 1.4.11 non-text contrast ≥ 3:1
        },
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#9ca3af', // WCAG 1.4.11 non-text contrast ≥ 3:1
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '12px',
          fontWeight: 600,
          height: 'auto',
          padding: '4px 2px',
        },
        filled: {
          '&.MuiChip-colorSuccess': {
            backgroundColor: '#dcfce7',
            color: '#16a34a',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: '#fef3c7',
            color: '#d97706',
          },
          '&.MuiChip-colorError': {
            backgroundColor: '#fee2e2',
            color: '#dc2626',
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: '#CCE0F5',
            color: '#0e7490',
          },
          '&.MuiChip-colorPrimary': {
            backgroundColor: '#CCE0F5',
            color: '#0e7490',
          },
          '&.MuiChip-colorDefault': {
            backgroundColor: '#f3f4f6',
            color: '#374151',
          },
        },
        outlined: {
          borderColor: '#9ca3af',
          '&:hover': {
            backgroundColor: '#f3f4f6',
          },
        },
        sizeSmall: {
          fontSize: '11px',
          padding: '2px 0',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '13px',
        },
        standardSuccess: {
          backgroundColor: '#dcfce7',
          color: '#166534',
          '& .MuiAlert-icon': {
            color: '#22c55e',
          },
        },
        standardWarning: {
          backgroundColor: '#fef3c7',
          color: '#92400e',
          '& .MuiAlert-icon': {
            color: '#f59e0b',
          },
        },
        standardError: {
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          '& .MuiAlert-icon': {
            color: '#ef4444',
          },
        },
        standardInfo: {
          backgroundColor: '#CCE0F5',
          color: '#164e63',
          '& .MuiAlert-icon': {
            color: '#0066CC',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '2px solid #9ca3af',
        },
        indicator: {
          backgroundColor: '#0066CC',
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          color: '#525252',   // WCAG AA 7.0:1 on white
          textTransform: 'none',
          padding: '12px 20px',
          minHeight: 48,
          '&.Mui-selected': {
            color: '#004C99',
            fontWeight: 600,
          },
          '&:hover': {
            color: '#374151',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f9fafb',
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#374151',
            fontSize: '13px',
            borderBottom: '2px solid #9ca3af',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          padding: '16px 20px',
          borderBottom: '1px solid #9ca3af',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f9fafb',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: '#9ca3af',
        },
        bar: {
          borderRadius: 4,
        },
        colorPrimary: {
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#0066CC',
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: '#f59e0b',
        },
        iconEmpty: {
          color: '#9ca3af',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#9ca3af',
            },
            '&:hover fieldset': {
              borderColor: '#d1d5db',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0066CC',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1), 0 8px 10px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '18px',
          fontWeight: 600,
          padding: '20px 24px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 20px',
          gap: 8,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1f2937',
          fontSize: '12px',
          padding: '8px 12px',
          borderRadius: 6,
        },
        arrow: {
          color: '#1f2937',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: '#f3f4f6',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: '#E6F0FA',
            '&:hover': {
              backgroundColor: '#CCE0F5',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--sidebar)',
          color: 'var(--sidebar-foreground)',
          boxShadow: 'none',
          borderBottom: '1px solid var(--border)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--sidebar)',
          color: 'var(--sidebar-foreground)',
          borderRight: '1px solid var(--border)',
        },
      },
    },
  },
};

// Crea tema con localizzazione italiana
export const theme = createTheme(themeOptions, itIT);
export const lightTheme = theme;

// Dark theme
const darkThemeOptions: ThemeOptions = {
  ...themeOptions,
  palette: {
    ...themeOptions.palette,
    mode: 'dark',
    primary: {
      main: '#fafafa',
      light: '#fafafa',
      dark: '#e5e5e5',
      contrastText: '#171717',
    },
    secondary: {
      main: '#262626',
      light: '#404040',
      dark: '#171717',
      contrastText: '#fafafa',
    },
    background: {
      default: '#0a0a0a',
      paper: '#171717',
    },
    text: {
      primary: '#fafafa',
      secondary: '#a3a3a3',
      disabled: '#525252',
    },
    divider: '#262626',
    error: teamSystemColors.error,
    warning: teamSystemColors.warning,
    success: teamSystemColors.success,
    info: {
      main: '#fafafa',
      light: '#262626',
      dark: '#e5e5e5',
      contrastText: '#171717',
    },
  },
  components: {
    ...themeOptions.components,
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#171717',
          borderRadius: 12,
          border: '1px solid #262626',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: '#262626',
          },
        },
      },
    },
  },
};

export const darkTheme = createTheme(darkThemeOptions, itIT);

// Export colori per uso diretto
export { teamSystemColors };
